import { getErrorMessage } from "../utils/commonUtils";
import {
  useCreateSettingsMutation,
  useDeleteSettingMutation,
  useEditSettingsMutation,
  useGetSettingsByIdMutation,
  useGetSettingsMutation,
  useUploadCompanyLogoMutation,
} from "../redux/settings/settingsApiSlice";
import { toast } from "react-toastify";
import { useAppDispatch } from "../redux/hooks";
import { setSystemSettings } from "../redux/settings/settingsSlice";
import { parseSystemSettings } from "../utils/moduleAccessUtils";

export default function useSettings() {
  const dispatch = useAppDispatch();
  const [getSettings, { isLoading: getSettingsLoading }] =
    useGetSettingsMutation();
  const [createSettings, { isLoading: createSettingsLoading }] =
    useCreateSettingsMutation();
  const [editSettings, { isLoading: editSettingsLoading }] =
    useEditSettingsMutation();
  const [deleteSetting, { isLoading: deleteSettingLoading }] =
    useDeleteSettingMutation();
  const [getSettingsById, { isLoading: getSettingsByIdLoading }] =
    useGetSettingsByIdMutation();
  const [uploadCompanyLogo, { isLoading: uploadCompanyLogoLoading }] =
    useUploadCompanyLogoMutation();

  const refreshSystemSettingsAPI = async () => {
    try {
      const response: any = await getSettings({}).unwrap();
      const rawSettings = response?.result ?? [];
      const parsedSettings = parseSystemSettings(rawSettings);

      dispatch(setSystemSettings(parsedSettings));

      return parsedSettings;
    } catch (err: any) {
      console.error("Failed to refresh system settings:", err);
      return null;
    }
  };

  const getSettingsAPI = async (data: any) => {
    try {
      const response: any = await getSettings(data).unwrap();
      return response.result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      throw new Error(message);
    }
  };

  const getSettingsByIdAPI = async (data: any) => {
    try {
      const response: any = await getSettingsById(data).unwrap();
      return response.result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      throw new Error(message);
    }
  };

  const createSettingsAPI = async (data: any) => {
    try {
      const response: any = await createSettings(data).unwrap();
      toast.success("Success! Setting has been Created.");
      // Refresh persisted settings on create
      await refreshSystemSettingsAPI();
      return response.result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      throw new Error(message);
    }
  };

  const editSettingsAPI = async (data: any) => {
    try {
      const response: any = await editSettings(data).unwrap();
      toast.success("Success! Setting has been Updated.");
      // Refresh persisted settings on update
      await refreshSystemSettingsAPI();
      return response.result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      throw new Error(message);
    }
  };

  const deleteSettingAPI = async (data: any) => {
    try {
      const response: any = await deleteSetting(data).unwrap();
      toast.success("Success! Setting has been Deleted.");
      // Refresh persisted settings on delete
      await refreshSystemSettingsAPI();
      return response.result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      throw new Error(message);
    }
  };

  const uploadCompanyLogoApi = async (data: any) => {
    try {
      const response = await uploadCompanyLogo(data).unwrap();
      return response;
    } catch (err: any) {
      const message = getErrorMessage(err);
      throw new Error(message);
    }
  };

  return {
    getSettingsAPI,
    getSettingsLoading,
    createSettingsLoading,
    createSettingsAPI,
    editSettingsAPI,
    editSettingsLoading,
    deleteSettingAPI,
    deleteSettingLoading,
    getSettingsByIdAPI,
    getSettingsByIdLoading,
    uploadCompanyLogoApi,
    uploadCompanyLogoLoading,
    refreshSystemSettingsAPI,
  };
}
